import React, { useEffect, useState } from "react";
import { Link, useLocation, Navigate } from "react-router-dom";
import { api } from "@/lib/api";
import { CITY_META } from "@/lib/cityMeta";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import HostelCard from "@/components/HostelCard";
import SearchBar from "@/components/SearchBar";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import {
  ArrowRight, MapPin, GraduationCap, ShieldCheck, IndianRupee, Building2,
} from "lucide-react";

// Match ?slug=kota → city key "Kota"
function findCity(slug) {
  const key = Object.keys(CITY_META).find((k) => CITY_META[k].slug === slug);
  return key ? { key, meta: CITY_META[key] } : null;
}

export default function CityPage() {
  const { pathname } = useLocation();
  const citySlug = pathname.replace(/^\//, "").toLowerCase();
  const match = findCity(citySlug);
  const [hostels, setHostels] = useState([]);
  const [stats, setStats] = useState({ count: 0, minPrice: null });

  useEffect(() => {
    if (!match) return;
    api.get(`/hostels?city=${match.key}`).then((r) => {
      setHostels(r.data);
      const prices = r.data.map((h) => h.price_per_bed).filter(Boolean);
      setStats({ count: r.data.length, minPrice: prices.length ? Math.min(...prices) : null });
    });
    // SEO: set title + meta description dynamically
    if (match?.meta) {
      document.title = `Hostels in ${match.key} — ${match.meta.tagline} · HostelNow`;
      let meta = document.querySelector('meta[name="description"]');
      if (!meta) {
        meta = document.createElement("meta");
        meta.name = "description";
        document.head.appendChild(meta);
      }
      meta.content = `Book verified student hostels in ${match.key}. ${match.meta.intro.slice(0, 140)}…`;
    }
    return () => { document.title = "TheHostelNow.com — Verified Stays. No Hidden Charges. Ever."; };
  }, [match]);

  if (!match) return <Navigate to="/hostels" replace />;
  const { key: city, meta } = match;

  return (
    <div className="min-h-screen bg-[#FBF5EA]">
      <Navbar />

      {/* Hero */}
      <section className="relative overflow-hidden bg-stone-900 text-white">
        <img
          src={meta.hero_bg}
          alt=""
          className="absolute inset-0 w-full h-full object-cover opacity-30"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-orange-900/80 via-stone-900/70 to-stone-900" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="text-xs tracking-[0.3em] uppercase font-bold text-amber-500">
            {meta.tagline}
          </div>
          <h1 data-testid="city-hero-title" className="mt-3 font-display font-black text-5xl sm:text-6xl lg:text-7xl tracking-tighter">
            Hostels in {city}
          </h1>
          <p className="mt-6 max-w-2xl text-slate-300 text-lg leading-relaxed">{meta.intro}</p>

          <div className="mt-8 flex flex-wrap gap-6 text-sm">
            <div className="flex items-center gap-2">
              <Building2 size={18} className="text-amber-500" />
              <span className="font-semibold">{stats.count}</span> verified hostels
            </div>
            {stats.minPrice != null && (
              <div className="flex items-center gap-2">
                <IndianRupee size={18} className="text-amber-500" />
                From <span className="font-semibold">₹{stats.minPrice}</span> / bed / night
              </div>
            )}
            <div className="flex items-center gap-2">
              <ShieldCheck size={18} className="text-amber-500" />
              Aadhaar KYC verified
            </div>
          </div>

          <div className="mt-10 max-w-3xl">
            <SearchBar />
          </div>
        </div>
      </section>

      {/* Neighborhoods + Coaching hubs */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 grid md:grid-cols-2 gap-8">
        <div className="bg-white border border-slate-200 rounded-2xl p-8">
          <div className="flex items-center gap-2 text-xs tracking-[0.2em] uppercase font-bold text-orange-800">
            <GraduationCap size={14} /> Top coaching in {city}
          </div>
          <h2 className="mt-3 font-display font-bold text-2xl">Study near the best institutes</h2>
          <div className="mt-5 flex flex-wrap gap-2">
            {meta.coaching_hubs.map((c) => (
              <span
                key={c}
                className="text-sm font-semibold px-3 py-1.5 rounded-full bg-orange-50 text-orange-800 border border-orange-100"
              >
                {c}
              </span>
            ))}
          </div>
        </div>
        <div className="bg-white border border-slate-200 rounded-2xl p-8">
          <div className="flex items-center gap-2 text-xs tracking-[0.2em] uppercase font-bold text-amber-600">
            <MapPin size={14} /> Popular areas
          </div>
          <h2 className="mt-3 font-display font-bold text-2xl">Where students stay in {city}</h2>
          <ul className="mt-5 space-y-2">
            {meta.neighborhoods.map((n) => (
              <li key={n} className="text-slate-700 flex items-center gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-amber-600" /> {n}
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Hostels list */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
        <div className="flex items-end justify-between mb-6">
          <div>
            <div className="text-xs tracking-[0.25em] uppercase font-bold text-amber-600">Available now</div>
            <h2 className="mt-2 font-display font-black text-3xl sm:text-4xl tracking-tighter">
              {stats.count} hostels in {city}
            </h2>
          </div>
          <Link to={`/hostels?city=${city}`} className="text-sm font-semibold text-orange-800 hover:gap-3 inline-flex items-center gap-2 transition-all">
            View all filters <ArrowRight size={16} />
          </Link>
        </div>
        {hostels.length === 0 ? (
          <div className="bg-white border border-dashed border-slate-300 rounded-2xl p-16 text-center text-slate-500">
            No hostels listed yet in {city}. Check back soon.
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {hostels.slice(0, 6).map((h) => <HostelCard key={h.id} h={h} />)}
          </div>
        )}
      </section>

      {/* FAQs */}
      <section className="bg-white border-y border-slate-200 py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-xs tracking-[0.25em] uppercase font-bold text-amber-600">Frequently asked</div>
          <h2 className="mt-2 font-display font-black text-4xl sm:text-5xl tracking-tighter">
            About hostels in {city}
          </h2>
          <Accordion type="single" collapsible className="mt-8">
            {meta.faqs.map((f, i) => (
              <AccordionItem key={f.q} value={`item-${i}`} data-testid={`faq-${citySlug}-${i}`}>
                <AccordionTrigger className="font-display font-bold text-lg text-left">{f.q}</AccordionTrigger>
                <AccordionContent className="text-slate-600 leading-relaxed">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16 text-center">
        <h2 className="font-display font-black text-4xl sm:text-5xl tracking-tighter">
          Ready to book your bed in {city}?
        </h2>
        <p className="mt-3 text-slate-600 max-w-xl mx-auto">
          Verified hostels · Aadhaar KYC · Beds from ₹{stats.minPrice || 250} · Instant confirmation.
        </p>
        <Link to={`/hostels?city=${city}`}>
          <Button className="mt-8 rounded-full bg-orange-800 hover:bg-orange-800 text-white h-12 px-8 gap-2 active:scale-95 transition-transform">
            Browse all {city} hostels <ArrowRight size={16} />
          </Button>
        </Link>
      </section>

      <Footer />
    </div>
  );
}
